import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx";

import { useGameContext } from "@/contexts/game-context";
import { Button } from "../button";
import { useBreakpoint } from "@/hooks/use-breakpoint";

type InputButtonProps = {
  kind: string;
  disabled?: boolean;
};

export const InputButton = ({ kind, disabled = false }: InputButtonProps) => {
  switch (kind) {
    case "yes":
      return YesInputButton({ disabled });
    case "no":
      return NoInputButton({ disabled });
    case "next":
      return NextInputButton({ disabled });
    case "replay":
      return ReplayInputButton({ disabled });
    case "no_response":
      return NoResponseInputButton({ disabled });
    case "incorrect":
      return IncorrectInputButton({ disabled });
    case "approximation":
      return ApproximationInputButton({ disabled });
    case "correct":
      return CorrectInputButton({ disabled });
    case "skip_assessment":
      return SkipAssessmentInputButton({ disabled });
  }

  return null;
};

const YesInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { yes },
  } = useGameContext();

  return (
    <button
      className={clsx(
        "rounded-full ring-[3px] ring-white text-gray-800 font-semibold size-16",
        {
          "bg-primary-green-200": disabled,
          "bg-primary-green-300 hover:bg-primary-green-400": !disabled,
        },
      )}
      onClick={() => yes()}
      disabled={disabled}
    >
      <FontAwesomeIcon icon="check" fixedWidth size="xl" />
    </button>
  );
};

const NoInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { no },
  } = useGameContext();

  return (
    <button
      className={clsx(
        "rounded-full ring-[3px] ring-white text-gray-800 font-semibold size-16",
        {
          "bg-red-200": disabled,
          "bg-red-300 hover:bg-red-400": !disabled,
        },
      )}
      onClick={() => no()}
      disabled={disabled}
    >
      <FontAwesomeIcon icon="times" fixedWidth size="xl" />
    </button>
  );
};

const NextInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { next },
  } = useGameContext();

  return (
    <button
      className={clsx(
        "rounded-full ring-[3px] ring-white text-gray-800 font-semibold size-16",
        {
          "bg-primary-green-200": disabled,
          "bg-primary-green-300 hover:bg-primary-green-400": !disabled,
        },
      )}
      onClick={() => next()}
      disabled={disabled}
    >
      <FontAwesomeIcon icon="arrow-right" fixedWidth size="xl" />
    </button>
  );
};

const ReplayInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { replay },
  } = useGameContext();

  return (
    <button
      className={clsx(
        "rounded-full ring-[3px] ring-white text-gray-800 font-semibold size-16",
        {
          "bg-primary-blue-200": disabled,
          "bg-primary-blue-300 hover:bg-primary-blue-400": !disabled,
        },
      )}
      onClick={() => replay()}
      disabled={disabled}
    >
      <FontAwesomeIcon icon="rotate-right" fixedWidth size="xl" />
    </button>
  );
};

const NoResponseInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { noResponse },
  } = useGameContext();

  return (
    <button
      className={clsx(
        "rounded-full ring-[3px] ring-white text-gray-800 font-semibold size-16",
        {
          "bg-gray-200": disabled,
          "bg-gray-300 hover:bg-gray-400": !disabled,
        },
      )}
      onClick={() => noResponse()}
      disabled={disabled}
    >
      <FontAwesomeIcon icon="minus" fixedWidth size="xl" />
    </button>
  );
};

const IncorrectInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { incorrect },
  } = useGameContext();

  return (
    <button
      className={clsx(
        "rounded-full ring-[3px] ring-white text-gray-800 font-semibold size-16",
        {
          "bg-red-200": disabled,
          "bg-red-300 hover:bg-red-400": !disabled,
        },
      )}
      onClick={() => incorrect()}
      disabled={disabled}
    >
      <FontAwesomeIcon icon="times" fixedWidth size="xl" />
    </button>
  );
};

const ApproximationInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { approximation },
  } = useGameContext();

  return (
    <button
      className={clsx(
        "rounded-full ring-[3px] ring-white text-gray-800 font-semibold size-16",
        {
          "bg-primary-orange-200": disabled,
          "bg-primary-orange-300 hover:bg-primary-orange-400": !disabled,
        },
      )}
      onClick={() => approximation()}
      disabled={disabled}
    >
      <div className="text-3xl font-semibold">~</div>
    </button>
  );
};

const CorrectInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { correct },
  } = useGameContext();

  return (
    <button
      className={clsx(
        "rounded-full ring-[3px] ring-white text-gray-800 font-semibold size-16",
        {
          "bg-primary-green-200": disabled,
          "bg-primary-green-300 hover:bg-primary-green-400": !disabled,
        },
      )}
      onClick={() => correct()}
      disabled={disabled}
    >
      <FontAwesomeIcon icon="check" fixedWidth size="xl" />
    </button>
  );
};

const SkipAssessmentInputButton = ({ disabled }: Omit<InputButtonProps, "kind">) => {
  const {
    input: { skipAssessment },
  } = useGameContext();

  const isTabletOrDesktop = useBreakpoint("lg");

  return (
    <Button
      type="button"
      className="w-full h-full p-2 rounded-lg ring-[3px] ring-white text-white bg-primary-blue-400 hover:bg-primary-blue-500"
      disabled={disabled}
      onClick={() => skipAssessment()}
    >
      {isTabletOrDesktop ? "Skip Pre and Post-Assessment Questions" : "Skip"}
    </Button>
  );
};
